"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Link from "@/components/ui/LocaleLink";
import { useTranslations, useLocale } from "next-intl";
import type { ProfessionalFull } from "@/lib/queries/professionals";
import type { SearchItem } from "@/lib/affiliate-match";
import ProfessionalListCard from "@/components/ui/ProfessionalListCard";
import AffiliateListCard from "@/components/affiliates/AffiliateListCard";
import type { Locale } from "@/lib/locale";
import { suggestCategoryForQuery } from "@/lib/search-autocomplete";
import { getCategoryLabel, normalizeCategory, type DirectoryCategory } from "@/lib/categories";
import { formatLocation } from "@/lib/provinces";

const PAGE_SIZE = 20;

const itemKey = (item: SearchItem<ProfessionalFull>) =>
  item.kind === "pro" ? `pro-${item.pro.slug}` : `affiliate-${item.affiliate.id}`;

interface SearchResultsProps {
  /** Page 1: professionals with any affiliates merged in at the paid/free boundary. */
  initialItems: SearchItem<ProfessionalFull>[];
  total: number;
  searchParams: Record<string, string>;
  /** Server-computed "Recommended" language boost (logged-in clients only),
   *  echoed into the load-more request so paged results keep page 1's order. */
  boostLanguage?: string;
  /** Server-computed: chat mode is `on` (the per-user allowlist is checked on /chat itself, never here). */
  showAssistantCta?: boolean;
  /** Public listings per category, for "Did you mean": a category with none is
   *  never suggested. Empty = counts unknown, and then nothing is filtered. */
  categoryCounts?: Record<string, number>;
}

export default function SearchResults({
  initialItems,
  total,
  searchParams,
  boostLanguage,
  showAssistantCta = false,
  categoryCounts = {},
}: SearchResultsProps) {
  const [items, setItems] = useState(initialItems);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialItems.length < total);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const locale = useLocale() as Locale;
  const t = useTranslations("search");

  // Reset when server re-renders with new search params
  useEffect(() => {
    setItems(initialItems);
    setHasMore(initialItems.length < total);
  }, [initialItems, total]);

  // The offset counts EVERY rendered item, affiliates included: the server
  // merges them into the whole ranked list before paging, so its offsets do too.
  const offsetRef = useRef(initialItems.length);
  useEffect(() => { offsetRef.current = items.length; }, [items.length]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    const offset = offsetRef.current;
    const params = new URLSearchParams(searchParams);
    params.set("offset", String(offset));
    params.set("limit", String(PAGE_SIZE));
    if (boostLanguage) params.set("boostLanguage", boostLanguage);
    params.set("locale", locale);

    try {
      const res = await fetch(`/api/search?${params.toString()}`);
      if (!res.ok) { setHasMore(false); return; }
      const data = (await res.json()) as { items: SearchItem<ProfessionalFull>[]; total: number };
      // De-duplicated: if an affiliate is activated or paused while the reader
      // scrolls, the server's merged list shifts by one and a page edge can
      // repeat an item (review 2026-09-15). Never render it twice.
      setItems((prev) => {
        const seen = new Set(prev.map(itemKey));
        return [...prev, ...data.items.filter((item) => !seen.has(itemKey(item)))];
      });
      setHasMore(offset + data.items.length < data.total);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, searchParams, boostLanguage, locale]);

  // Intersection Observer — trigger loadMore when sentinel enters viewport
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  // Stable key for the fade wrapper: when the URL filters change, this changes
  // and the wrapper remounts, replaying `panel-enter-fade` on the new content.
  // Loaded-more items get appended without remounting (key unchanged), so the
  // animation doesn't fire on infinite-scroll appends.
  const searchKey = JSON.stringify(searchParams);
  // Active category filter (canonical) — when set, cards badge the matched
  // category for pros that surfaced via a secondary category.
  const activeCategory = normalizeCategory(searchParams.category ?? "");
  // Active location filter — when set, cards lead with the MATCHED city for
  // multi-office pros that surfaced via a non-main office (same principle).
  const activeLocation = searchParams.location ?? "";

  const renderItem = (item: SearchItem<ProfessionalFull>) =>
    item.kind === "pro" ? (
      <ProfessionalListCard key={item.pro.slug} pro={item.pro} locale={locale} activeCategory={activeCategory} activeLocation={activeLocation} />
    ) : (
      <AffiliateListCard key={`affiliate-${item.affiliate.id}`} affiliate={item.affiliate} locale={locale} activeCategory={activeCategory} activeLocation={activeLocation} />
    );

  const proCount = items.filter((item) => item.kind === "pro").length;

  if (proCount === 0) {
    // Turn the dead-end into next steps using only the params we already hold —
    // no extra query, no dependency. (a) "Did you mean" corrects a typo'd
    // free-text term to the nearest category; (b) loosen-filter links drop one
    // narrowing param at a time. See bettersearch.md #4 / Design System §3.20.
    const base: Record<string, string> = {};
    for (const [k, v] of Object.entries(searchParams)) {
      if (v && k !== "offset" && k !== "limit") base[k] = v;
    }
    const href = (params: Record<string, string>) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v) p.set(k, v);
      const qs = p.toString();
      return qs ? `/search?${qs}` : "/search";
    };
    const omit = (...keys: string[]) =>
      href(Object.fromEntries(Object.entries(base).filter(([k]) => !keys.includes(k))));

    const q = (base.q ?? "").trim();
    const catValue = normalizeCategory(base.category ?? "");
    const catLabel = catValue ? getCategoryLabel(catValue, locale) : "";
    const locLabel = base.location ? formatLocation(base.location, locale) : "";

    // "Did you mean" — only for a typo'd free-text term that points at a
    // different category than the one already filtered.
    const dymRaw = q ? suggestCategoryForQuery(q, locale, categoryCounts) : null;
    const dymValue = dymRaw ? normalizeCategory(dymRaw) : "";
    const dym = dymValue && dymValue !== catValue ? dymValue : "";

    // Loosen options, most useful first; only those whose filter is active.
    const broaden: { key: string; label: string; href: string }[] = [];
    if (base.location)
      broaden.push({ key: "loc", label: locLabel ? t("searchBeyond", { location: locLabel }) : t("searchAllLocations"), href: omit("location") });
    if (base.language)
      broaden.push({ key: "lang", label: t("removeLanguageFilter"), href: omit("language") });
    // "View all {category}" only escapes a free-text term — a location/language
    // filter already has its own "remove" link above, which lands the same place.
    if (catValue && q)
      broaden.push({ key: "cat", label: t("viewAllCategory", { category: catLabel }), href: href({ category: catValue }) });
    if (q) broaden.push({ key: "q", label: t("clearSearchTerm"), href: omit("q") });

    const narrowingCount = ["q", "category", "location", "language"].filter((k) => base[k]).length;

    // Always leave one escape hatch: if a lone filter (e.g. an empty category)
    // produced no loosen link above, fall back to a full reset.
    if (broaden.length === 0 && narrowingCount >= 1)
      broaden.push({ key: "all", label: t("clearFilters"), href: "/search" });

    return (
      <div key={searchKey} className="panel-enter-fade">
      {/* No professional matched, but an affiliate in the category asked
          for did: it comes first (owner, 2026-09-15), the "no professionals"
          message and its next steps follow. */}
      {items.length > 0 && (
        <div className="flex flex-col gap-13">{items.map(renderItem)}</div>
      )}
      <div className="p-55 text-center">
        <p className="font-serif text-xl text-navy">{t(items.length > 0 ? "noOtherResults" : "noResults")}</p>

        {dym ? (
          <p className="mt-13 text-sm text-gray-600">
            {t.rich("didYouMean", {
              category: getCategoryLabel(dym as DirectoryCategory, locale),
              cat: (chunks) => (
                <Link
                  href={href({ ...base, category: dym, q: "" })}
                  className="font-semibold text-navy underline decoration-gold decoration-2 underline-offset-2 hover:opacity-70"
                >
                  {chunks}
                </Link>
              ),
            })}
          </p>
        ) : (
          <p className="mt-8 text-sm text-gray-600">{t("tryAdjusting")}</p>
        )}

        {broaden.length > 0 && (
          <div className="mt-21 flex flex-wrap items-center justify-center gap-x-21 gap-y-8">
            {broaden.map((b) => (
              <Link
                key={b.key}
                href={b.href}
                className="text-sm font-medium text-navy underline hover:opacity-70"
              >
                {b.label}
              </Link>
            ))}
          </div>
        )}

        {narrowingCount >= 2 && (
          <Link
            href="/search"
            className="mt-21 inline-block text-xs font-semibold uppercase tracking-widest text-gray-500 hover:text-navy"
          >
            {t("clearAllFilters")}
          </Link>
        )}

        {showAssistantCta && (
          <p className="mt-21 text-sm text-gray-600">
            <Link href="/chat" className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-[3px] hover:text-gold">
              {t("askAssistant")}
            </Link>
          </p>
        )}

      </div>

      </div>
    );
  }

  return (
    <div>
      <div key={searchKey} className="panel-enter-fade flex flex-col gap-13">
        {items.map(renderItem)}
      </div>

      {/* Sentinel — triggers next page load when scrolled into view */}
      {hasMore && (
        <div ref={sentinelRef} aria-hidden="true" className="h-1" />
      )}

      {loading && (
        <div className="mt-13 flex items-center justify-center gap-8 py-21">
          <span className="h-[14px] w-[14px] animate-spin rounded-full border-2 border-navy/20 border-t-navy" />
          <span className="text-sm text-gray-500">{t("loadingMore")}</span>
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <p className="mt-13 py-21 text-center text-sm text-gray-400">
          {t("allResultsShown")}
        </p>
      )}
    </div>
  );
}
